import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import type {BaseGenerateAiSubtitlesOptionProps} from './base-generate-ai-subtitles-option.types';
import styles from './base-generate-ai-subtitles-option.module.scss';
import {ContextualMenuButtonOption} from '@Components/contextual-menu/components/contextual-menu-button-option';
import {AiSubtitleGenerationLanguageSelector} from '@Panels/ai-subtitle-panel/components/ai-subtitle-generation-language-selector/ai-subtitle-generation-language-selector';
import {ContextualMenuDropdownType} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {AiTranscriptAction} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {Tag, TagType} from '@Components/tag';
import type {ButtonItemsStorageType} from '@Components/contextual-menu/components/button-items';
import {ContextualButtonItemType} from '@Components/contextual-menu/components/button-items';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {Size, Type} from '@Components/button';
import {noop} from '@Utils/general.util';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {AiSubtitleState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {useAppSelector} from '@/hooks';
import {showAiSubtitlesUpsellDialog} from '@Libraries/ai-transcript.library';

export function BaseGenerateAiSubtitlesOption({className = '', showNewTag = false, ...props}: BaseGenerateAiSubtitlesOptionProps): ReactElement<any> {
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });

  const getNewTag = (): ReactElement<any> | undefined => {
    return showNewTag ? (
      <Tag className={'radius-round spacing-p-l-2 spacing-p-r-2 spacing-p-t-1 spacing-p-b-1'} text={window.i18next.t('pmwjs_new')} type={TagType.SECONDARY} />
    ) : undefined;
  };

  const getButtonText = (): string => {
    switch (props.ctaState) {
      case AiTranscriptAction.GENERATE:
        return window.i18next.t('pmwjs_generate');
      case AiTranscriptAction.REGENERATE:
        return window.i18next.t('pmwjs_regenerate');
      case AiTranscriptAction.GENERATED:
        return window.i18next.t('pmwjs_generated');
      default:
        throw new Error(`Unhandled ctaState: ${props.ctaState}`);
    }
  };

  const generateAiSubtitles = (): void => {
    if (!isPremium.current) {
      void showAiSubtitlesUpsellDialog();
      return;
    }

    props.onGenerateClicked();
  };

  const getData = (): ButtonItemsStorageType => {
    switch (props.ctaState) {
      case AiTranscriptAction.GENERATE:
      case AiTranscriptAction.REGENERATE:
        return [
          {
            isLoading: aiSubtitleState === AiSubtitleState.GENERATING,
            isDisabled: false,
            type: ContextualButtonItemType.AI_BUTTON,
            id: 'generate-ai-subtitles',
            variant: isPremium.current ? AIButtonVariant.SUBDUED : AIButtonVariant.PREMIUM_SUBDUED,
            buttonSize: Size.SMALL,
            buttonIcon: 'icon-ai-subtitles',
            buttonText: getButtonText(),
            loadingStateText: window.i18next.t('pmwjs_generating'),
            onClick: generateAiSubtitles,
          },
        ];
      case AiTranscriptAction.GENERATED:
        return [
          {
            type: ContextualButtonItemType.CUSTOM_BUTTON,
            icon: 'icon-check',
            buttonSize: Size.SMALL,
            text: getButtonText(),
            buttonType: Type.SUCCESS,
            id: 'ai-subtitles-generated',
            onClick: noop,
          },
        ];
      default:
        throw new Error(`Unhandled ctaState: ${props.ctaState}`);
    }
  };

  return (
    <ContextualMenuButtonOption tag={getNewTag()} className={className} isInlineOption label={window.i18next.t('pmwjs_ai_subtitles')} id="generate-ai-subtitles" data={getData()}>
      <AiSubtitleGenerationLanguageSelector
        selectorClasses={styles.selectorMaxWidth}
        showLanguageIcon={false}
        dropdownSelectorType={ContextualMenuDropdownType.GHOST}
        isDisabled={aiSubtitleState === AiSubtitleState.GENERATING || props.ctaState === AiTranscriptAction.GENERATED}
      />
    </ContextualMenuButtonOption>
  );
}
